import { Grid, Theme, createStyles, makeStyles, TextField, CircularProgress, Tooltip, IconButton } from '@material-ui/core';
import React, { useState, useEffect } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useTranslation } from 'react-i18next';
import { useFormContext, FieldError } from 'react-hook-form';
import { getValidator, colors } from '../../../Constants/constants';
import { AutocompleteDto } from '../../../API/Interfaces/ICorporationDtos';
import FileServiceAPI from '../../../API/Services/FileServiceAPI';
import { DeleteIcon } from '../../../Layout/Components/Icons';

export interface IGraphicProps {
	title: string;
	name: string;
	options: any;
	isRequired?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		container: {
			display: 'flex',
			flexDirection: 'column',
			minWidth: '269px',
		},
		picturePreviewContainer: {
			display: 'flex',
			boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.25)',
			height: '214px',
			padding: '10px',
			justifyContent: 'center',
			alignItems: 'center',
			marginTop: '18px',
		},
		pictureThumbnail: {
			minWidth: '269px',
			display: 'flex',
			justifyContent: 'center',
		},
		popupIndicator: {
			color: colors.main,
		},
		title: {
			fontSize: '18px',
			fontWeight: 'bold',
			lineHeight: '21px',
			letterSpacing: '0.015em',
		},
		image: {
			maxHeight: '214px',
			width: 'auto',
			maxWidth: '269px',
		},
	})
);

const Graphic = (props: IGraphicProps) => {
	const { t } = useTranslation();
	const { errors, register, setValue, getValues, reset, triggerValidation } = useFormContext();
	const { title, name, options, isRequired = false } = props;
	const [isLoading, setIsLoading] = useState(false);
	const classes = useStyles();

	const [selectedItem, setSelectedItem] = useState<AutocompleteDto>({
		name: '',
		id: getValues()[name] || '',
	});

	const formValidators = {
		graphic: getValidator(t, null, null, isRequired, isRequired ? 1 : 0),
	};

	const [imageStr, setImageStr] = useState<any | null>();

	const fetchAndSetFile = async (id: number) => {
		setIsLoading(true);
		const fileResponse = await FileServiceAPI.GetFileUrl(id);
		setImageStr(fileResponse.data.image);
		setIsLoading(false);
	};

	useEffect(() => {
		if (options && options.find((l) => l.id === Number(getValues()[name]))) {
			fetchAndSetFile(getValues()[name]);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [options]);

	return (
		<>
			<input name={name} ref={register(formValidators.graphic)} hidden={true} />
			<div className={classes.container}>
				<div style={{ display: 'flex', justifyContent: 'space-between' }}>
					<p className={classes.title}>{title}</p>
					{imageStr && !isLoading && (
						<Tooltip title={t('table.body.deleteTooltip') || ''}>
							<IconButton
								onClick={() => {
									setImageStr('');
									setSelectedItem({ name: '', id: '' });
									reset({ ...getValues(), [name]: '' });
									triggerValidation(name);
								}}
								style={{ marginRight: '-10px' }}
							>
								<DeleteIcon isSelected={true} />
							</IconButton>
						</Tooltip>
					)}
				</div>

				<Autocomplete
					disableClearable
					classes={{
						popupIndicator: classes.popupIndicator,
					}}
					noOptionsText={t('common.form.none')}
					loadingText={t('common.form.loading')}
					getOptionSelected={(option, value) => option.name === value.name}
					onChange={(e: any, v: any) => {
						if (v?.id) {
							setValue(name, v?.id);
							setSelectedItem(v);
							fetchAndSetFile(v.id);
							triggerValidation(name);
						}
					}}
					getOptionLabel={(option) => option.name || ''}
					options={options}
					value={options.find((l) => l.id === selectedItem.id) || null}
					renderInput={(params) => (
						<TextField
							error={errors[name] !== undefined}
							helperText={(errors[name] as FieldError)?.message}
							{...params}
							placeholder={t('common.photo')}
							fullWidth
							InputProps={{
								...params.InputProps,
								endAdornment: <React.Fragment>{params.InputProps.endAdornment}</React.Fragment>,
							}}
							autoFocus
						/>
					)}
					getOptionDisabled={(option) => option.id === undefined}
				/>

				<Grid className={classes.picturePreviewContainer}>
					<Grid className={classes.pictureThumbnail}>
						{isLoading ? (
							<CircularProgress color='inherit' size={20} />
						) : imageStr ? (
							<img src={imageStr} className={classes.image} alt={t('common.photo')} />
						) : (
							<></>
						)}
					</Grid>
				</Grid>
			</div>
		</>
	);
};

export default Graphic;
